import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

export interface StripeAccount {
  id: string;
  instructor_id: string;
  stripe_account_id: string;
  country: string;
  currency: string;
  charges_enabled: boolean;
  payouts_enabled: boolean;
  details_submitted: boolean;
  requirements: string[];
  requirements_due_at: string | null;
  onboarding_link: string | null;
  onboarding_link_expires_at: string | null;
  created_at: string;
  updated_at: string;
}

export function useStripeConnect() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Fetch current Stripe account status
  const { data: account, isLoading, error } = useQuery({
    queryKey: ['stripe-account', user?.id],
    queryFn: async (): Promise<StripeAccount | null> => {
      if (!user) return null;

      const { data, error } = await supabase
        .from('stripe_accounts')
        .select('*')
        .eq('instructor_id', user.id)
        .maybeSingle();

      if (error) throw error;

      return data;
    },
    enabled: !!user,
    // The instructor onboarding wizard mounts each step as its own component,
    // and several of them reach this query — directly, and again through
    // `usePayoutCurrency`, which derives the payout currency from the account.
    // With no staleTime every one of those observers fired a background refetch
    // on mount, so a step transition refetched an account row that had not
    // changed. Five minutes, matching `useUserProfile` and the country query
    // beside it. `startOnboarding` and `refreshStatus` both invalidate this
    // key, and invalidation ignores staleTime, so a real status change still
    // lands immediately.
    staleTime: 5 * 60 * 1000,
  });

  // Trigger onboarding flow
  const startOnboardingMutation = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error('No user');

      const token = (await supabase.auth.getSession()).data.session?.access_token;
      if (!token) throw new Error('No auth token');

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/stripe-connect-onboard`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({}),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to start onboarding');
      }

      return response.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['stripe-account', user?.id] });
      // Return the onboarding URL for redirect
      return data.onboarding_url;
    },
  });

  // Refresh account status from Stripe
  const refreshStatusMutation = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error('No user');

      const token = (await supabase.auth.getSession()).data.session?.access_token;
      if (!token) throw new Error('No auth token');

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/stripe-connect-refresh`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({}),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to refresh status');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stripe-account', user?.id] });
    },
  });

  // Get Express Dashboard login link
  const getLoginLinkMutation = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error('No user');

      const token = (await supabase.auth.getSession()).data.session?.access_token;
      if (!token) throw new Error('No auth token');

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/stripe-connect-login-link`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({}),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to get login link');
      }

      return response.json();
    },
  });

  return {
    // State
    account,
    isLoading,
    error: error?.message || null,
    isOnboarded: account?.charges_enabled && account?.payouts_enabled,

    // Actions
    startOnboarding: startOnboardingMutation.mutateAsync,
    isStartingOnboarding: startOnboardingMutation.isPending,
    onboardingError: startOnboardingMutation.error?.message || null,

    refreshStatus: refreshStatusMutation.mutateAsync,
    isRefreshingStatus: refreshStatusMutation.isPending,

    getLoginLink: getLoginLinkMutation.mutateAsync,
    isGettingLoginLink: getLoginLinkMutation.isPending,
  };
}
